import Image from "next/image";
import Link from "next/link";
import { useRouter } from "next/router";

import { useEffect, useMemo, useState } from "react";
import { OverlayTrigger, Tooltip } from "react-bootstrap";

import classNames from "classnames";
import { Entity } from "models/entity";
import numeral from "numeral";
import { twMerge } from "tailwind-merge";

import { faCaretDown } from "@fortawesome/free-solid-svg-icons/faCaretDown";
import { faCaretUp } from "@fortawesome/free-solid-svg-icons/faCaretUp";
import { faCube } from "@fortawesome/free-solid-svg-icons/faCube";
import { faEllipsisV } from "@fortawesome/free-solid-svg-icons/faEllipsisV";

import AddToWatchlistButton from "components/AddToWatchlistButton";
import ContactIRButton from "components/ContactIRButton";
import CustomCard from "components/CustomCard";
import EntityAnnoucementComposeButton from "components/EntityAnnoucementComposeButton";
import NextDividendExText from "components/EntityPageLayout/EntityPageDividend/NextDividendEx/text";
import NextEarningText from "components/EntityPageLayout/EntityPageEstimates/NextEarning/text";
import { entityCorporateOfficersUrl } from "components/EntityPageLayout/EntityPageInvestorRelations";
import { entityRelatedIPUrl } from "components/EntityPageLayout/EntityPageRelatedIP";
import { entitySmartScoreUrl } from "components/EntitySmartScore";
import FontAwesomeIcon from "components/FontAwesomeIcon";
import ShareButton from "components/ShareButton";
import TextMore from "components/TextMore";
import { useApi } from "hooks/useApi";
import useAuthentication from "hooks/useAuthentication";
import useMediaQuery from "hooks/useMediaQuery";
import { baseFetcher } from "utils/baseFetcher";
import { emberAppBaseUrl } from "utils/envVariables";
import { sectors } from "consts/sectors";

import styles from "./style.module.scss";

export const entityCompactUrl = (security: string) =>
  security ? `/api/v2/price-api/get-compact?ticker=${security}` : "";

interface Props {
  entity: Entity;
  style?: any | "v2";
}

const EntityCard = ({ entity, style }: Props) => {
  const { mobile } = useMediaQuery();

  const router = useRouter();
  const {
    authenticated,
    account: loggedInAccount,
    managedEntity,
  } = useAuthentication();

  const url = entityCompactUrl(entity.security);
  const { data: entityData, isValidating } = useApi(url, baseFetcher, {
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
  });
  const { data: entityRelatedIP } = useApi(
    entityRelatedIPUrl(entity.slug, 1),
    baseFetcher
  );
  const { data: entityCorporateOfficers } = useApi(
    entityCorporateOfficersUrl(entity.slug, 1),
    baseFetcher
  );

  const { data: entitySmartScore } = useApi(
    entity.smartScore ? null : entitySmartScoreUrl(entity.slug),
    baseFetcher
  );
  const smartScore = entitySmartScore || entity.smartScore;

  const getShortSecurity = function (security: string) {
    if (!security || !security.match) {
      return security;
    }

    const match = security
      .replace(/\s+/g, " ")
      .match(/([\d\-&_A-Z_/]+ [A-Z]{2}) EQUITY/i);
    if (match) {
      return match[1];
    } else {
      return security;
    }
  };

  const [showTitle, setShowTitle] = useState(true);

  const shortSecurity = getShortSecurity(entity?.security);
  const name = entity?.prettyName;
  const entityUrl = `${emberAppBaseUrl}/entities/${entity.slug}`;
  const title = `Check this out: ${name} ${entityUrl}`;

  const totalAnalysis =
    (entity?.totalBullish || 0) + (entity?.totalBearish || 0);
  const totalViews = entity?.totalViews || 0;
  const totalDiscussions = entity?.totalDiscussions || 0;

  const description = entity?.entityDescription;

  const shareBtnOptions = useMemo(
    () => ({
      email: {
        link: `mailto:?subject=\u272a Smartkarma | ${name} ${shortSecurity}&body=${title}`,
      },
      clipboard: {
        message: title,
      },
      privateMessage: {
        message: `Check this out: <a href="${entityUrl}">${name}</a>`,
      },
      whatsapp: {
        link: `https://web.whatsapp.com/send?text=${title}`,
      },
      twitter: {
        link: `https://twitter.com/share?url=${url}&text=${title}&via=smartkarma`,
      },
      linkedIn: {
        link: `https://www.linkedin.com/shareArticle?mini=true&url=${url}&source=Smartkarma`,
      },
    }),
    [entityUrl, name, shortSecurity, title, url]
  );

  const actions = useMemo(() => {
    if (
      loggedInAccount?.isCorporateOfficer &&
      managedEntity?.id === entity.id
    ) {
      if (mobile) {
        return (
          <div>
            <EntityAnnoucementComposeButton />
          </div>
        );
      }

      return (
        <div>
          <Link
            className={twMerge(
              "rounded-full px-[1.25em] py-[0.75em]",
              "border-none bg-smart-teal hover:bg-[#1d8988] text-[13px] text-white hover:text-white"
            )}
            href={`/settings/company`}
          >
            Edit Company Profile
          </Link>
        </div>
      );
    }

    if (style === "v2") {
      return (
        <div className="flex space-x-[16px]">
          <AddToWatchlistButton
            entity={entity}
            className="text-[16px] md:text-[15px] md:px-[1.1em] md:py-[0.75em]"
            activeClassName="md:px-[0.72em]"
            hideTextOnActive
          />
          <ContactIRButton slug={entity?.slug} />
        </div>
      );
    }

    return (
      <div
        className={twMerge(styles.headerActions, "items-start md:items-center")}
      >
        <AddToWatchlistButton entity={entity} />
        <span className={styles.share}>
          <ShareButton
            entity={entity}
            options={shareBtnOptions}
            columnView
            noBorder
          />
        </span>
      </div>
    );
  }, [
    entity,
    loggedInAccount?.isCorporateOfficer,
    managedEntity?.id,
    mobile,
    shareBtnOptions,
    style,
  ]);

  const more = useMemo(() => {
    if (
      entityCorporateOfficers?.data?.length ||
      entityRelatedIP?.data?.length
    ) {
      return (
        <button
          className="border-0 bg-transparent relative top-[-14px]"
          onClick={() => {
            router.push(`/entities/${entity?.slug}/more`);
          }}
        >
          <FontAwesomeIcon icon={faEllipsisV} color="#999" />
        </button>
      );
    }
  }, [
    entityCorporateOfficers?.data?.length,
    entityRelatedIP?.data?.length,
    router,
    entity?.slug,
  ]);

  const icon = sectors.find((s) => s.name === entity?.sector)?.icon || faCube;

  useEffect(() => {
    if (!isValidating) {
      setShowTitle(Boolean(entityData?.price));
    }
  }, [entityData?.price, isValidating]);

  if (style === "v2") {
    return (
      <CustomCard
        className={twMerge(
          "p-0",
          "mx-[-5px] mt-[-10px] mb-[-10px] rounded-none",
          "md:mx-0 md:mt-0 md:mb-[15px] md:rounded-xl",
          "bg-gradient-to-br from-[#1F1F20] to-[#0D4567]",
          "md:bg-none",
          "text-white",
          "md:text-active-black"
        )}
      >
        <div className="">
          <div
            className={twMerge(
              "w-full h-[32px] md:h-auto rounded-t-xl",
              "bg-gradient-to-r from-[#163243] to-[#0D4567]",
              "flex"
            )}
          >
            <div
              className={twMerge(
                "text-inactive-gray text-[14px]",
                "border border-solid border-inactive-gray",
                "px-[4px] md:py-[1px]",
                "ml-[15px] my-[5px]",
                "md:mt-[18px] md:mb-[14px] md:ml-[204px]"
              )}
            >
              ENTITY
            </div>
            {!loggedInAccount?.isCorporateOfficer &&
              smartScore?.countryAndSectorScore && (
                <div
                  className={twMerge(
                    "flex-1",
                    "md:mt-[14px]",
                    "px-[30px]",
                    "text-right",
                    "text-[#C6B47B] text-[20px] font-bold"
                  )}
                >
                  SMARTSCORE: {smartScore.countryAndSectorScore}/5
                </div>
              )}
          </div>
          <div className={twMerge("md:pl-[33px] md:pr-[33px]")}>
            <div
              className={twMerge(
                "absolute shadow-lg",
                "rounded-xl",
                "w-[108px] h-[108px] mt-[-22px] left-0 right-0 mx-auto",
                "md:w-[152px] md:h-[152px] md:mt-[-38px] md:left-auto md:right-auto md:mx-0",
                "border border-solid border-inactive-gray",
                "bg-white"
              )}
            >
              {entity?.logoUrl?.large ? (
                <Image
                  className={twMerge("rounded-xl")}
                  src={entity?.logoUrl?.large}
                  width={mobile ? 106 : 150}
                  height={mobile ? 106 : 150}
                  alt={name}
                  priority
                />
              ) : (
                <FontAwesomeIcon
                  icon={icon}
                  className="text-[86px] md:text-[130px]"
                />
              )}
            </div>
            <div
              className={twMerge(
                "w-full",
                "pt-[100px]",
                "md:pl-[170px] md:pt-[18px]"
              )}
            >
              <div className={twMerge("flex", "flex-col", "md:flex-row")}>
                <h1 className="flex-1 leading-[1.5] mb-0">
                  <div
                    className={twMerge(
                      "flex",
                      "flex-col text-center",
                      "md:flex-row md:text-left md:space-x-2"
                    )}
                  >
                    <div
                      className={twMerge(
                        "text-[14px] font-medium",
                        "md:text-[18px] md:text-[#555]"
                      )}
                    >
                      {entity?.displayName ||
                        entity?.prettyName ||
                        entity?.shortName}{" "}
                      {shortSecurity && `(${shortSecurity})`}
                    </div>
                  </div>

                  <div
                    className={twMerge(
                      "md:text-active-black",
                      "text-[26px] font-bold text-center",
                      "md:text-[30px] md:text-left",
                      "md:min-h-[45px]"
                    )}
                  >
                    {entity.showPriceQuote &&
                      entityData?.price &&
                      authenticated && (
                        <>
                          <span className={twMerge("mr-[6px]")}>
                            <span className={""}>
                              {numeral(entityData?.price || 0).format("0,0.00")}{" "}
                            </span>
                            <span className={""}>{entityData?.currency}</span>
                          </span>
                          <span
                            className={twMerge(
                              entityData?.percentage < 0
                                ? "text-bearish-red"
                                : "text-bullish-green"
                            )}
                          >
                            <FontAwesomeIcon
                              className={""}
                              icon={
                                entityData?.percentage < 0
                                  ? faCaretDown
                                  : faCaretUp
                              }
                            />{" "}
                            {numeral(
                              Math.abs(entityData?.percentage) || 0
                            ).format("0,0.00")}
                            %
                          </span>
                        </>
                      )}
                  </div>
                </h1>

                <div className="text-right flex-col hidden md:flex">
                  <div
                    className={twMerge(
                      "mr-[-6px] mb-[5px] flex-1 flex justify-end"
                    )}
                  >
                    <div>{actions}</div>
                  </div>
                </div>
              </div>
              <div className="flex flex-col md:flex-row items-center font-normal">
                <NextEarningText entity={entity} />
                <NextDividendExText entity={entity} />
              </div>
            </div>

            <hr className="bg-gray-400 hidden md:block" />

            <div
              className={twMerge(
                "flex",
                "flex-col",
                "md:flex-row md:pb-[13px]"
              )}
            >
              <div>
                <div className="flex items-center">
                  <div
                    className={twMerge(
                      "flex space-x-[38px]",
                      "my-[20px]",
                      "md:my-0",
                      "md:min-w-[245px]"
                    )}
                  >
                    {totalAnalysis ? (
                      <div className="text-center min-w-[55px]">
                        <div className="text-[16px] font-black">
                          {totalAnalysis > 999
                            ? numeral(totalAnalysis).format("0.0a")
                            : totalAnalysis}
                        </div>
                        <div className="text-[11px]">Analysis</div>
                      </div>
                    ) : null}
                    {totalViews ? (
                      <div className="text-center min-w-[55px]">
                        <div className="text-[16px] font-black">
                          {totalViews > 999
                            ? numeral(totalViews).format("0.0a")
                            : totalViews}
                        </div>
                        <div className="text-[11px]">Views</div>
                      </div>
                    ) : null}
                    {totalDiscussions ? (
                      <div className="text-center min-w-[55px]">
                        <div className="text-[16px] font-black">
                          {totalDiscussions > 999
                            ? numeral(totalDiscussions).format("0.0a")
                            : totalDiscussions}
                        </div>
                        <div className="text-[11px]">Discussions</div>
                      </div>
                    ) : null}
                  </div>

                  <div className="ml-[42px] whitespace-nowrap text-intelligent-silver">
                    {entity?.sector && (
                      <span className={styles.sector}>{entity?.sector}</span>
                    )}
                    {entity?.sector && entity?.country && " • "}
                    {entity?.country && (
                      <span className={styles.country}>{entity?.country}</span>
                    )}
                  </div>
                </div>
              </div>

              <div className="md:flex-1 flex justify-end min-w-0">
                {/* add min-w-0 to fix truncated text */}
                <TextMore text={description} type="v2" className="mt-[10px]" />
              </div>

              <div className={twMerge("md:hidden", "mb-[20px] flex")}>
                {actions}
              </div>
            </div>
          </div>
        </div>
      </CustomCard>
    );
  }

  if (mobile) {
    return (
      <div className="p-[15px] pb-0">
        {showTitle && (
          <h1 className="mb-[8px] text-[16px] font-medium text-[#555]">
            {entity.prettyName} ({entity.shortSecurity})
          </h1>
        )}
        <div
          className={twMerge(
            "flex justify-between items-start",
            !entityData?.price && "!items-center"
          )}
        >
          {!isValidating ? (
            entityData?.price ? (
              <div className="flex flex-col">
                <h2
                  className={twMerge(
                    styles.sharePrice,
                    "share-price",
                    "inline text-[24px]",
                    "mr-[15px] md:mr-[20px] mb-0"
                  )}
                >
                  <span className={styles.price}>
                    {numeral(entityData?.price || 0).format("0,0.00")}{" "}
                  </span>
                  <span className={styles.currency}>
                    {entityData?.currency}
                  </span>
                </h2>
                <span
                  className={twMerge(
                    "flex items-center font-semibold",
                    entityData?.percentage < 0 && "text-bearish-red",
                    entityData?.percentage > 0 && "text-bullish-green",
                    (!entityData?.percentage || entityData?.percentage == 0) &&
                      "text-jade"
                  )}
                >
                  <FontAwesomeIcon
                    icon={entityData?.percentage < 0 ? faCaretDown : faCaretUp}
                  />{" "}
                  {numeral(Math.abs(entityData?.percentage) || 0).format(
                    "0,0.00"
                  )}
                  %
                </span>
              </div>
            ) : (
              <div className="">
                <h1 className="mb-[8px] text-[16px] font-medium text-[#555] relative mt-[-16px]">
                  {entity.prettyName} ({entity.shortSecurity})
                </h1>
              </div>
            )
          ) : (
            <div className="flex-col">
              <div className="w-[120px] h-[20px] bg-inactive-gray animate-pulse mb-[8px]"></div>
              <div className="w-[30px] h-[20px] bg-inactive-gray animate-pulse"></div>
            </div>
          )}
          <div className="flex">
            <div className="flex flex-col text-center">
              <AddToWatchlistButton entity={entity} />
              <OverlayTrigger
                rootClose
                placement={mobile ? "bottom" : "top"}
                trigger="click"
                overlay={
                  <Tooltip className="w-[200px]">
                    <div className="text-left">
                      <p>
                        {entity.country} &gt; {entity.sector}
                      </p>
                      <p className="mb-0">{entity.entityDescription}</p>
                    </div>
                  </Tooltip>
                }
              >
                <span className="mt-[12px] text-[12px] text-smart-teal">
                  See Description
                </span>
              </OverlayTrigger>
            </div>
            {more}
          </div>
        </div>

        <div className="flex flex-col font-normal">
          <NextEarningText entity={entity} />
          <NextDividendExText entity={entity} />
        </div>
      </div>
    );
  }

  return (
    <>
      <div
        className={classNames(
          styles.entityHeaderCard,
          mobile && styles.mobileLayout,
          mobile && "min-h-[125px]"
        )}
      >
        {!mobile && (
          <div className={styles.headerImg}>
            {entity?.logoUrl?.large ? (
              <Image
                src={entity?.logoUrl?.large}
                alt={entity?.prettyName}
                width={55}
                height={55}
                priority
              />
            ) : (
              <FontAwesomeIcon icon={icon} className={styles.icon} />
            )}
          </div>
        )}
        <section className={styles.headerDetails}>
          <h1 className="flex">
            <span className="flex-1 mt-[5px]">
              {entity?.displayName || entity?.prettyName || entity?.shortName}
              {shortSecurity && ` - ${shortSecurity}`}
            </span>
            {(!entityData?.price || !entity.showPriceQuote) &&
              mobile &&
              actions}
          </h1>
          <div className={styles.entityInfo}>
            {entityData?.price && entity.showPriceQuote ? (
              <section
                className={classNames(
                  styles.group,
                  (entityData?.price || isValidating) && styles.withPrice
                )}
              >
                {entity.showPriceQuote && entityData?.price && (
                  <>
                    <h2
                      className={twMerge(
                        styles.sharePrice,
                        "share-price",
                        "inline text-[15px] font-normal",
                        "mr-[15px] md:mr-[20px] mb-0"
                      )}
                    >
                      <span className={styles.price}>
                        {numeral(entityData?.price || 0).format("0,0.00")}{" "}
                      </span>
                      <span className={styles.currency}>
                        {entityData?.currency}
                      </span>
                    </h2>
                    <span
                      className={`${styles.change} ${
                        styles[entityData?.percentage < 0 ? "down" : "up"]
                      }`}
                    >
                      <FontAwesomeIcon
                        className={styles.icon}
                        icon={
                          entityData?.percentage < 0 ? faCaretDown : faCaretUp
                        }
                      />{" "}
                      {numeral(Math.abs(entityData?.percentage) || 0).format(
                        "0,0.00"
                      )}
                      %
                    </span>
                  </>
                )}
                {mobile && actions}
              </section>
            ) : (
              <div className={classNames(styles.group, styles.withPrice)}>
                <div className=""></div>
              </div>
            )}
            <div className={twMerge(styles.group)}>
              {entity?.sector && (
                <span className={styles.sector}>{entity?.sector}</span>
              )}
              {entity?.country && (
                <span className={styles.country}>{entity?.country}</span>
              )}
            </div>
          </div>
        </section>
        {!mobile && actions}
      </div>
    </>
  );
};

export default EntityCard;
